import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { CalendarDays } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { ru } from 'react-day-picker/locale';

// The month grid ships its own structural stylesheet; the look (tokens) is
// re-mapped under `.date-picker__popover .rdp-root` in theme.css.
import 'react-day-picker/style.css';

import { calendarDateToIso, formatDisplayDate, isoToCalendarDate, isValidDisplayDate, parseDisplayDate } from '../lib/date';

interface DatePickerProps {
  /** ISO `YYYY-MM-DD` or null (no date). */
  value: string | null;
  onChange: (iso: string | null) => void;
  /** Visible label text — the format hint lives here, never in the field (F-13b). */
  label: string;
  id?: string;
  /** Extra classes for the INPUT (kept on the input, not the wrapper). */
  className?: string;
  disabled?: boolean;
  onCommit?: () => void;
  /** Overrides the default `date-input` test id (the FilterBar filters). */
  testId?: string;
}

/**
 * Controlled `dd.mm.yyyy` text input storing ISO (F-13b) + a calendar
 * popover (F-26).
 *
 * A native `<input type="date">` would paint Chrome's own `dd.mm.yyyy` ghost
 * into an empty field; here the placeholder is always EMPTY and the format is
 * carried by the label/`title`. Typing commits as soon as the value parses,
 * clearing commits null; an unparsable value is flagged with `aria-invalid`
 * and reverted on blur.
 *
 * The `CalendarDays` toggle next to the input opens a russian month grid:
 * it starts on the selected date (or today), past dates are selectable
 * (deadlines can be backdated) and picking a day commits the same
 * `onChange(iso)` as typing, then closes. The popover is `position: fixed`
 * and placed from the toggle's client rect — an absolute child would be
 * clipped by `.table-wrap`'s `overflow-x: auto` (and by the modal body).
 */
export function DatePicker({ value, onChange, label, id, className, disabled, onCommit, testId = 'date-input' }: DatePickerProps): ReactElement {
  const [text, setText] = useState(() => formatDisplayDate(value));
  const [open, setOpen] = useState(false);
  // The visible month of the grid (controlled so «open» always resets it).
  const [month, setMonth] = useState<Date>(() => isoToCalendarDate(value) ?? new Date());
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const focused = useRef(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  // An outside change (modal opened on another project) replaces the text,
  // unless the user is mid-edit.
  useEffect(() => {
    if (!focused.current) setText(formatDisplayDate(value));
  }, [value]);

  const invalid = text.trim() !== '' && !isValidDisplayDate(text);

  const commit = (next: string) => {
    const parsed = parseDisplayDate(next);
    if (parsed === undefined) return; // invalid mid-typing: wait for a full value
    if (parsed === null) {
      if (value !== null) onChange(null);
      return;
    }
    if (parsed !== value) onChange(parsed);
  };

  const closeCalendar = useCallback((refocus: boolean) => {
    setOpen(false);
    setPos(null);
    if (refocus) buttonRef.current?.focus();
  }, []);

  const openCalendar = useCallback(() => {
    setMonth(isoToCalendarDate(value) ?? new Date());
    setPos(null);
    setOpen(true);
  }, [value]);

  // Place the fixed popover next to the toggle; flip it above when the space
  // below runs out (last rows / the modal scrolled to the bottom).
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const button = buttonRef.current;
      const popover = popoverRef.current;
      if (!button || !popover) return;
      const rect = button.getBoundingClientRect();
      const width = popover.offsetWidth;
      const height = popover.offsetHeight;
      const margin = 8;
      const gap = 6;
      const left = Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin));
      const below = rect.bottom + gap;
      const fitsBelow = below + height <= window.innerHeight - margin;
      const fitsAbove = rect.top - gap - height >= margin;
      setPos({ top: fitsBelow || !fitsAbove ? below : rect.top - gap - height, left });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  // Close on Escape (captured BEFORE the modal's own handler, so only the
  // popover closes), on a press outside the control, and on scroll — a fixed
  // popover must never drift away from its button. Escape returns focus to
  // the toggle, an outside press moves focus wherever the user pressed.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      closeCalendar(true);
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) closeCalendar(false);
    };
    const onScroll = () => closeCalendar(false);
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown, true);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, closeCalendar]);

  /** A picked day is a commit — same channel as typing — then the popover closes. */
  const handleSelect = (date: Date | undefined) => {
    if (!date) return;
    const iso = calendarDateToIso(date);
    setText(formatDisplayDate(iso));
    closeCalendar(true);
    if (iso !== value) onChange(iso);
  };

  return (
    <div className="date-picker" ref={wrapRef}>
      <label className="sr-only" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        className={['input', 'date-input', invalid ? 'date-input--invalid' : '', className ?? '']
          .filter(Boolean)
          .join(' ')}
        value={text}
        placeholder=""
        title={invalid ? `${label}: ожидается дд.мм.гггг` : label}
        aria-label={label}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        data-testid={testId}
        onChange={(event) => {
          setText(event.target.value);
          commit(event.target.value);
        }}
        onFocus={() => {
          focused.current = true;
        }}
        onBlur={() => {
          focused.current = false;
          if (isValidDisplayDate(text)) {
            commit(text);
            setText(formatDisplayDate(parseDisplayDate(text) ?? null));
          } else {
            // Revert a half-typed or broken value to the stored date.
            setText(formatDisplayDate(value));
          }
          onCommit?.();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            if (isValidDisplayDate(text)) commit(text);
            event.currentTarget.blur();
          }
        }}
      />
      <button
        type="button"
        className="date-picker__toggle"
        aria-label="Открыть календарь"
        title="Открыть календарь"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        disabled={disabled}
        data-testid="date-picker-button"
        ref={buttonRef}
        onClick={() => (open ? closeCalendar(true) : openCalendar())}
      >
        <CalendarDays size={14} aria-hidden="true" />
      </button>
      {open ? (
        <div
          id={popoverId}
          className="date-picker__popover"
          role="dialog"
          aria-label="Выбор даты"
          data-testid="date-picker-popover"
          ref={popoverRef}
          // Hidden (off the measurement, never painted) until the layout
          // effect has placed it — no flicker at 0,0.
          style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
        >
          <DayPicker
            locale={ru}
            mode="single"
            selected={isoToCalendarDate(value)}
            month={month}
            onMonthChange={setMonth}
            onSelect={handleSelect}
            showOutsideDays
          />
        </div>
      ) : null}
    </div>
  );
}
